import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import { doctorService, hospitalService, treatmentService } from '../services/api';
import { hospitalNameToSlug } from '../utils/hospitalUtils';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [results, setResults] = useState({ doctors: [], hospitals: [], treatments: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Smart search categorization
  const categorizeSearch = (query) => {
    const lowerQuery = query.toLowerCase();
    
    // Common treatment/speciality keywords
    const treatmentKeywords = [
      'surgery', 'transplant', 'cardiology', 'neurology', 'oncology', 'orthopedic',
      'plastic', 'bariatric', 'cancer', 'heart', 'brain', 'liver', 'kidney',
      'joint', 'spine', 'bypass', 'replacement', 'therapy', 'treatment'
    ];
    
    // Common hospital keywords
    const hospitalKeywords = [
      'hospital', 'medical center', 'clinic', 'apollo', 'fortis', 'max', 'aiims',
      'manipal', 'medanta', 'artemis', 'institute', 'healthcare', 'care'
    ];
    
    // Common doctor keywords (titles and specialties)
    const doctorKeywords = [
      'dr', 'doctor', 'surgeon', 'physician', 'consultant', 'specialist',
      'cardiologist', 'neurologist', 'oncologist', 'orthopedist', 'pediatrician'
    ];
    
    // Check what type of search this is
    const isTreatmentSearch = treatmentKeywords.some(keyword => lowerQuery.includes(keyword));
    const isHospitalSearch = hospitalKeywords.some(keyword => lowerQuery.includes(keyword));
    const isDoctorSearch = doctorKeywords.some(keyword => lowerQuery.includes(keyword));
    
    return {
      isTreatmentSearch,
      isHospitalSearch,
      isDoctorSearch,
      priority: isTreatmentSearch ? 'treatments' : 
                isHospitalSearch ? 'hospitals' : 
                isDoctorSearch ? 'doctors' : 'mixed'
    };
  };

  const sortResultsByPriority = (doctors, hospitals, treatments, searchType) => {
    switch (searchType.priority) {
      case 'treatments':
        return { 
          primary: treatments, 
          secondary: [...doctors, ...hospitals],
          order: ['treatments', 'doctors', 'hospitals']
        };
      case 'hospitals':
        return { 
          primary: hospitals, 
          secondary: [...treatments, ...doctors],
          order: ['hospitals', 'treatments', 'doctors']
        };
      case 'doctors':
        return { 
          primary: doctors, 
          secondary: [...treatments, ...hospitals],
          order: ['doctors', 'treatments', 'hospitals']
        };
      default:
        return { 
          primary: [...treatments, ...doctors, ...hospitals], 
          secondary: [],
          order: ['treatments', 'doctors', 'hospitals']
        };
    }
  };

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query.trim()) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch from all three sources
        const [doctorsResponse, hospitalsResponse, treatmentsResponse] = await Promise.all([
          doctorService.getAll({ search: query, limit: 50 }),
          hospitalService.getAll({ search: query, limit: 50 }),
          treatmentService.getPublished({ search: query, limit: 50 })
        ]);

        const doctors = doctorsResponse.data?.doctors || [];
        const hospitals = hospitalsResponse.data?.hospitals || [];
        const treatments = treatmentsResponse.data?.treatments || [];

        // Analyze search type and sort results
        const searchType = categorizeSearch(query);
        const sortedResults = sortResultsByPriority(doctors, hospitals, treatments, searchType);

        setResults({
          doctors,
          hospitals,
          treatments,
          searchType,
          sortedResults
        });
      } catch (error) {
        console.error('Search error:', error);
        setResults({ doctors: [], hospitals: [], treatments: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query]);

  const totalResults = results.doctors.length + results.hospitals.length + (results.treatments?.length || 0);

  const filteredResults = () => {
    switch (activeTab) {
      case 'doctors':
        return { doctors: results.doctors, hospitals: [], treatments: [] };
      case 'hospitals':
        return { doctors: [], hospitals: results.hospitals, treatments: [] };
      case 'treatments':
        return { doctors: [], hospitals: [], treatments: results.treatments || [] };
      default:
        return results;
    }
  };

  // Get ordered results for display
  const getOrderedResults = () => {
    if (!results.sortedResults) return filteredResults();
    
    const filtered = filteredResults();
    if (activeTab !== 'all') return filtered;

    // For 'all' tab, show results in smart order
    const ordered = { doctors: [], hospitals: [], treatments: [] };
    results.sortedResults.order.forEach(type => {
      if (type === 'doctors') ordered.doctors = filtered.doctors;
      if (type === 'hospitals') ordered.hospitals = filtered.hospitals;  
      if (type === 'treatments') ordered.treatments = filtered.treatments;
    });
    
    return ordered;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-xl text-blue-600">Searching...</div>
          </div>
        </main>
        <Footer />
      <FloatingChatButton />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Search Results for "{query}"
          </h1>
          <p className="text-gray-600">
            Found {totalResults} result{totalResults !== 1 ? 's' : ''}
            {results.searchType && (
              <span className="ml-2 text-blue-600">
                (Showing {results.searchType.priority} results first)
              </span>
            )}
          </p>
        </div>

        {/* Search Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All ({totalResults})
          </button>
          <button
            onClick={() => setActiveTab('doctors')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'doctors'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Doctors ({results.doctors.length})
          </button>
          <button
            onClick={() => setActiveTab('hospitals')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'hospitals'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Hospitals ({results.hospitals.length})
          </button>
          <button
            onClick={() => setActiveTab('treatments')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'treatments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Treatments ({results.treatments?.length || 0})
          </button>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {/* Treatments Results */}
          {getOrderedResults().treatments && getOrderedResults().treatments.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Treatments</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getOrderedResults().treatments.map((treatment) => (
                  <Link
                    key={treatment.id || treatment._id}
                    to={`/treatments/${treatment.slug}`}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100"
                  >
                    <div className="mb-4">
                      <img
                        src={treatment.headerImage || '/images/default-treatment.jpg'}
                        alt={treatment.title}
                        className="w-full h-40 rounded-lg object-cover mb-4"
                      />
                      <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600">
                        {treatment.title}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        {treatment.description && treatment.description.length > 150 
                          ? `${treatment.description.substring(0, 150)}...` 
                          : treatment.description}
                      </p>
                      {treatment.associatedSpecialities && treatment.associatedSpecialities.length > 0 && (
                        <p className="text-xs text-blue-600">
                          {treatment.associatedSpecialities.slice(0, 3).join(', ')}
                          {treatment.associatedSpecialities.length > 3 && '...'}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Doctors Results */}
          {getOrderedResults().doctors.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Doctors</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getOrderedResults().doctors.map((doctor) => (
                  <Link
                    key={doctor.id}
                    to={`/doctor-details/${doctor.id}`}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100"
                  >
                    <div className="flex items-center mb-4">
                      <img
                        src={doctor.image || '/images/default-doctor.jpg'}
                        alt={doctor.name}
                        className="w-16 h-16 rounded-full object-cover mr-4"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600">
                          {doctor.name}
                        </h3>
                        <p className="text-gray-600">{doctor.specialty}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-blue-600 font-medium">{doctor.hospital}</p>
                      <p className="text-sm text-gray-500">{doctor.experience}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Hospitals Results */}
          {getOrderedResults().hospitals.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Hospitals</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getOrderedResults().hospitals.map((hospital) => (
                  <Link
                    key={hospital.id}
                    to={`/hospital/${hospitalNameToSlug(hospital.name)}`}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-100"
                  >
                    <div className="mb-4">
                      <img
                        src={hospital.mainImage || '/images/default-hospital.jpg'}
                        alt={hospital.name}
                        className="w-full h-40 rounded-lg object-cover mb-4"
                      />
                      <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600">
                        {hospital.name}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">{hospital.country}</p>
                      {hospital.specialities && hospital.specialities.length > 0 && (
                        <p className="text-xs text-blue-600">
                          {hospital.specialities.slice(0, 3).join(', ')}
                          {hospital.specialities.length > 3 && '...'}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {totalResults === 0 && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-500 mb-6">
                  We couldn't find any doctors or hospitals matching "{query}". Try adjusting your search terms.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SearchResults;
